import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [pathname]);

  const solid = scrolled || pathname !== "/" || menuOpen;

  const links = [
    { to: "/shop", label: "Collection" },
    { to: "/projects", label: "Projects" },
    { to: "/genetics", label: "Genetics" },
    { to: "/social", label: "Media" },
    { to: "/newsletter", label: "Newsletter" },
  ];

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        solid ? "bg-black/95 md:bg-black/85 md:backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="group flex items-center">
          <span className="wordmark text-lg text-qbfg group-hover:text-qbprimary transition-colors">Quantum Boas</span>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => {
            const isHash = l.to.startsWith("/#");
            return (
              <Link
                key={l.to}
                to={isHash ? "/" : l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                onClick={(e) => {
                  if (isHash && pathname === "/") {
                    e.preventDefault();
                    document.querySelector(l.to.slice(1))?.scrollIntoView({ behavior: "smooth" });
                  }
                }}
                className="text-sm text-qbfg/70 hover:text-qbfg transition-colors relative after:absolute after:-bottom-1.5 after:left-0 after:w-0 after:h-px after:bg-qbprimary hover:after:w-full after:transition-all after:duration-300"
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        <button
          type="button"
          data-testid="mobile-menu-toggle"
          onClick={() => setMenuOpen((o) => !o)}
          className="md:hidden p-2 -mr-2 text-qbfg"
          aria-label="Toggle menu"
        >
          {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile dropdown */}
      <div
        data-testid="mobile-menu"
        className={`md:hidden overflow-hidden border-t transition-[max-height,opacity] duration-300 ${
          menuOpen ? "max-h-80 opacity-100 border-white/10 bg-black/95" : "max-h-0 opacity-0 border-transparent"
        }`}
      >
        <nav className="flex flex-col px-6 py-4">
          {links.map((l) => {
            const isHash = l.to.startsWith("/#");
            return (
              <Link
                key={l.to}
                to={isHash ? "/" : l.to}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                onClick={(e) => {
                  setMenuOpen(false);
                  if (isHash && pathname === "/") {
                    e.preventDefault();
                    setTimeout(() => document.querySelector(l.to.slice(1))?.scrollIntoView({ behavior: "smooth" }), 50);
                  }
                }}
                className="py-3 text-base text-qbfg/80 hover:text-qbprimary border-b border-white/5 last:border-0 transition-colors"
              >
                {l.label}
              </Link>
            );
          })}
        </nav>
      </div>
    </header>
  );
};
